import React from 'react';
import Editor from '@monaco-editor/react';

// The one way V3 shows or edits JSON: Monaco, the VS Code editor, on a black background (the same on the dark and the light page). Line
// numbers, fold arrows, syntax colours, the editor's own JSON checking, and search (Ctrl+F in the editor). @monaco-editor/react loads Monaco
// from its CDN, so nothing is bundled. Use it for any JSON a page shows or takes, read only or not; do not make a second way.
//   value     the JSON as text
//   onChange  called with the new text (leave it out for a read-only editor)
//   label     the editor's accessible name
//   hint      shown, faintly, while an editable editor is empty (Monaco has no placeholder of its own)

const LINE_HEIGHT = 19;
const CHROME_HEIGHT = 16;           // top and bottom padding Monaco reserves whatever the content

export const BLACK_THEME = 'v3-black';

export const defineBlackTheme = (monaco) => {
  monaco.editor.defineTheme(BLACK_THEME, {
    base: 'vs-dark',
    inherit: true,
    rules: [],
    colors: {
      'editor.background': '#000000',
      'editorGutter.background': '#000000',
      'editor.lineHighlightBackground': '#000000',
      'minimap.background': '#000000',
      'editorOverviewRuler.background': '#000000',
      'scrollbarSlider.background': '#3a3f4a80',
      'editorLineNumber.foreground': '#5f6677',
    },
  });
};

export const jsonText = (value) => (value === undefined || value === null ? '' : JSON.stringify(value, null, 2));

export default function JsonEditor({ value, onChange, label = 'JSON', hint, readOnly = false, maxHeight = 420 }) {
  const text = value === undefined || value === null ? '' : String(value);
  const height = Math.min(maxHeight, Math.max(readOnly ? 60 : 110, text.split('\n').length * LINE_HEIGHT + CHROME_HEIGHT));
  const editable = !readOnly && !!onChange;
  return (
    <div className="json-editor">
      {editable && !text && hint && <div className="json-editor-hint" aria-hidden="true">{hint}</div>}
      <Editor
        height={height}
        language="json"
        theme={BLACK_THEME}
        beforeMount={defineBlackTheme}
        value={text}
        onChange={(next) => { if (onChange) onChange(next === undefined ? '' : next); }}
        options={{
          ariaLabel: label,
          readOnly: !editable,
          domReadOnly: !editable,
          minimap: { enabled: false },
          folding: true,
          showFoldingControls: 'always',          // the default shows the arrows only on hover, which is easy to miss
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          fontSize: 12.5,
          tabSize: 2,
          insertSpaces: true,
          formatOnPaste: true,
          renderLineHighlight: 'none',
          wordWrap: 'on',
          automaticLayout: true,
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          scrollbar: { alwaysConsumeMouseWheel: false },
        }}
      />
    </div>
  );
}
